import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useCreateTimeEntryMutation,
  useDeleteTimeEntryMutation,
  usePauseTimerMutation,
  useResumeTimerMutation,
  useSwitchTimerItemMutation,
  useUpdateHeartbeatMutation,
  useUpdateTimeEntryMutation,
} from '@/hooks/mutations/useTimeEntryMutations'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import { useActiveTimerQuery } from '@/hooks/queries/useTimeEntriesQuery'
import { queryKeys } from '@/hooks/query-keys'
import { useDisplayClock } from '@/hooks/useDisplayClock'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { ApiError } from '@/lib/api/api-client'
import { timeEntryApi } from '@/lib/api/time-entries.api'
import { createLogger } from '@/lib/logger'
import { useTimeEntryRealtime } from '@/lib/realtime/features/useTimeEntryRealtime'
import { useOrganizationStore } from '@/store/organization.store'
import { createDateOnlyUTC } from '@/utils/date'
import { debounce } from '@/utils/debounce'
import { useQueryClient } from '@tanstack/react-query'

import {
  calendarDateInTimeZone,
  ModuleTypeEnum,
  PaginatedResponse,
  TimeEntry,
  TimeEntryLinkedModule,
  TimeEntryTypeEnum,
  UpdateTimeEntryInput,
} from '@scrambl/shared'

const logger = createLogger('components/time-entry/TimeEntryProvider')

interface ActiveTimeEntry extends TimeEntry {
  elapsed: number // in seconds (for timer display)
}

interface TimeEntryContextType {
  activeEntry: ActiveTimeEntry | null
  isTracking: boolean
  startTimer: (options?: {
    description?: string
    moduleType?: ModuleTypeEnum
    moduleId?: string
    moduleName?: string | null
    billable?: boolean
    roleId?: string | null
    linkedModules?: TimeEntryLinkedModule[]
    /** Time category; its billable default applies when billable is omitted. */
    categoryId?: string | null
  }) => Promise<void>
  stopTimer: (options?: {
    endTime?: Date
    description?: string
  }) => Promise<TimeEntry | null>
  pauseTimer: () => Promise<TimeEntry | null>
  resumeTimer: () => Promise<TimeEntry | null>
  switchTimerItem: (
    target: TimeEntryLinkedModule,
    options?: {
      linkedModules?: TimeEntryLinkedModule[]
      description?: string | null
      billable?: boolean
      roleId?: string | null
      categoryId?: string | null
    },
  ) => Promise<TimeEntry | null>
  discardTimer: () => Promise<void>
  updateActiveEntry: (updates: Partial<ActiveTimeEntry>) => Promise<void>
  clearTimerData: () => void
  isPaused: boolean
  isInitialized: boolean
}

const TimeEntryContext = createContext<TimeEntryContextType | null>(null)

// Calculate elapsed time considering pause state
export function calculateElapsed(timer: TimeEntry, now = Date.now()): number {
  if (!timer.startTime) return 0

  const startTime = new Date(timer.startTime).getTime()

  if (timer.isPaused && timer.pausedAt) {
    // If paused, calculate time up to pause point
    const pausedAt = new Date(timer.pausedAt).getTime()
    return Math.floor(
      (pausedAt - startTime - timer.pausedDuration * 1000) / 1000,
    )
  } else {
    // If running, calculate total time minus paused duration
    return Math.floor((now - startTime - timer.pausedDuration * 1000) / 1000)
  }
}

const HEARTBEAT_INTERVAL = 30000 // 30 seconds

export function TimeEntryProvider({ children }: { children: ReactNode }) {
  const { timezone } = useUserTimezone()
  const [activeEntry, setActiveEntry] = useState<ActiveTimeEntry | null>(null)
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null)
  const isLoadingRef = useRef(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const activeEntryRef = useRef<ActiveTimeEntry | null>(null)
  // Track pending heartbeat to prevent duplicate requests
  const heartbeatInFlightRef = useRef(false)
  const queryClient = useQueryClient()
  const createTimeEntryMutation = useCreateTimeEntryMutation()
  const updateTimeEntryMutation = useUpdateTimeEntryMutation()
  const deleteTimeEntryMutation = useDeleteTimeEntryMutation()
  const pauseTimerMutation = usePauseTimerMutation()
  const resumeTimerMutation = useResumeTimerMutation()
  const switchTimerItemMutation = useSwitchTimerItemMutation()
  const updateHeartbeatMutation = useUpdateHeartbeatMutation()
  // Use selectors to prevent unnecessary re-renders
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  const currentOrganizationId = currentOrganization?.id
  const isExternalUser = currentOrganization?.isExternalAccess === true
  const activeTimerQuery = useActiveTimerQuery({ enabled: !isExternalUser })
  const activeTimerFromServer = activeTimerQuery.data

  // Memoize the enabled check to prevent infinite loops
  const shouldFetchSettings = (() => {
    if (!currentOrganizationId) return false
    if (typeof window === 'undefined') return false
    return window.location.pathname !== '/organizations'
  })()

  // Fetch organization settings via TanStack Query (automatically handles fetching)
  useOrganizationSettingsQuery({
    enabled: shouldFetchSettings,
  })

  // Keep activeEntryRef in sync with state
  useEffect(() => {
    activeEntryRef.current = activeEntry
  }, [activeEntry])

  const clearLocalActiveTimerState = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }

    heartbeatInFlightRef.current = false
    activeEntryRef.current = null
    setActiveEntry(null)
  }

  const syncActiveTimerFromServer = async () => {
    try {
      const serverTimer = await timeEntryApi.getActiveTimer()
      queryClient.setQueryData(
        queryKeys.timeEntries.active(currentOrganizationId),
        serverTimer,
      )

      if (serverTimer?.id) {
        const elapsed = calculateElapsed(serverTimer)
        const nextEntry = {
          ...serverTimer,
          elapsed,
        }
        activeEntryRef.current = nextEntry
        setActiveEntry(nextEntry)
      } else {
        clearLocalActiveTimerState()
      }
    } catch (error) {
      logger.error('[TimeEntryProvider] Failed to sync active timer:', error)
    }
  }

  // Real-time event handlers
  const handleTimerStarted = useCallback(
    (timeEntry: TimeEntry) => {
      const elapsed = calculateElapsed(timeEntry)
      setActiveEntry({
        ...timeEntry,
        elapsed,
      })
    },
    [calculateElapsed],
  )

  const handleTimerStopped = (timeEntry: TimeEntry) => {
    // Clear active entry when timer is stopped
    if (activeEntryRef.current?.id === timeEntry.id) {
      setActiveEntry(null)
    }
  }

  const handleTimerPaused = useCallback(
    (timeEntry: TimeEntry) => {
      if (activeEntryRef.current?.id === timeEntry.id) {
        const elapsed = calculateElapsed(timeEntry)
        setActiveEntry({
          ...timeEntry,
          elapsed,
        })
      }
    },
    [calculateElapsed],
  )

  const handleTimerResumed = useCallback(
    (timeEntry: TimeEntry) => {
      if (activeEntryRef.current?.id === timeEntry.id) {
        const elapsed = calculateElapsed(timeEntry)
        setActiveEntry({
          ...timeEntry,
          elapsed,
        })
      }
    },
    [calculateElapsed],
  )

  const handleTimerUpdated = useCallback(
    (timeEntry: TimeEntry) => {
      if (activeEntryRef.current?.id === timeEntry.id) {
        const elapsed = calculateElapsed(timeEntry)
        setActiveEntry({
          ...timeEntry,
          elapsed,
        })
      }
    },
    [calculateElapsed],
  )

  const handleTimerDeleted = useCallback(
    (data: { id: string; userId: string }) => {
      if (activeEntryRef.current?.id === data.id) {
        setActiveEntry(null)
      }
    },
    [],
  )

  // Set up real-time synchronization
  useTimeEntryRealtime({
    enabled: !isExternalUser,
    onTimerStarted: handleTimerStarted,
    onTimerStopped: handleTimerStopped,
    onTimerPaused: handleTimerPaused,
    onTimerResumed: handleTimerResumed,
    onTimerUpdated: handleTimerUpdated,
    onTimerDeleted: handleTimerDeleted,
  })

  // Reset initialization when organization changes
  useEffect(() => {
    setIsInitialized(false)
  }, [currentOrganizationId])

  // Load active timer from database using query hook
  useEffect(() => {
    // Don't load timer if we're on the organizations page
    const isOnOrganizationsPage =
      typeof window !== 'undefined' &&
      window.location.pathname === '/organizations'

    if (!currentOrganization || isOnOrganizationsPage || isExternalUser) {
      // Skip timer loading when on organizations page or no organization selected
      clearLocalActiveTimerState()
      setIsInitialized(true)
      return
    }

    if (isLoadingRef.current || activeTimerQuery.isLoading) {
      return
    }

    // Sync active timer from server query to local state
    if (activeTimerFromServer && activeTimerFromServer.id) {
      const elapsed = calculateElapsed(activeTimerFromServer)
      setActiveEntry({
        ...activeTimerFromServer,
        elapsed,
      })
      setIsInitialized(true)
    } else if (
      activeTimerFromServer === null ||
      activeTimerQuery.isFetched ||
      activeTimerQuery.isError
    ) {
      clearLocalActiveTimerState()
      setIsInitialized(true)
    }
    // Depend on actual props, not function closure that's fresh every render
  }, [
    activeTimerFromServer,
    activeTimerQuery.isLoading,
    activeTimerQuery.isFetched,
    activeTimerQuery.isError,
    currentOrganization,
    isExternalUser,
  ])

  // Store heartbeat mutation in a ref to avoid re-running the effect when the hook re-renders
  const heartbeatMutationRef = useRef(updateHeartbeatMutation)
  heartbeatMutationRef.current = updateHeartbeatMutation

  // Set up heartbeat for active timer
  useEffect(() => {
    // Don't start heartbeat if no organization context - prevents 403 errors
    // We require currentOrganizationId to be set AND truthy (not just the effect re-running)
    if (
      !activeEntry ||
      activeEntry.isPaused ||
      !currentOrganizationId ||
      isExternalUser
    ) {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current)
        heartbeatRef.current = null
      }
      // Skip heartbeat when no organization context to prevent 403 errors
      return
    }

    const entryId = activeEntry.id
    // Capture the org ID at setup time to verify it matches when sending heartbeats
    const setupOrgId = currentOrganizationId

    // Handler for heartbeat results
    const handleHeartbeatResult = (result: {
      success: boolean
      active: boolean
    }) => {
      if (!result.active) {
        setActiveEntry(null)
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current)
          heartbeatRef.current = null
        }
      }
    }

    // Helper to send heartbeat with deduplication
    const sendHeartbeat = (isInitial = false) => {
      // Prevent duplicate requests
      if (heartbeatInFlightRef.current) {
        return
      }

      // Verify org context is still valid
      const currentOrgId =
        useOrganizationStore.getState().currentOrganization?.id
      if (!currentOrgId) {
        logger.warn(
          `[TimeEntryProvider] Skipping ${isInitial ? 'initial ' : ''}heartbeat - org context lost`,
        )
        return
      }

      // Verify the org hasn't changed (e.g., user switched orgs)
      if (currentOrgId !== setupOrgId) {
        logger.warn(
          '[TimeEntryProvider] Skipping heartbeat - organization changed. Setup:',
          setupOrgId,
          'Current:',
          currentOrgId,
        )
        return
      }

      heartbeatInFlightRef.current = true
      heartbeatMutationRef.current.mutate(entryId, {
        onSuccess: result => {
          heartbeatInFlightRef.current = false
          handleHeartbeatResult(result)
        },
        onError: error => {
          heartbeatInFlightRef.current = false
          if (error instanceof ApiError && error.status === 404) {
            // Entry was deleted server-side. If it's still our active entry,
            // clear local state and stop the heartbeat loop. If a different
            // entry is now active, the stale 404 is harmless — just ignore.
            if (activeEntryRef.current?.id === entryId) {
              clearLocalActiveTimerState()
              queryClient.setQueryData(
                queryKeys.timeEntries.active(currentOrganizationId),
                null,
              )
            }
            return
          }

          logger.error(
            `[TimeEntryProvider] ${isInitial ? 'Initial h' : 'H'}eartbeat failed:`,
            error,
          )
        },
      })
    }

    // Send initial heartbeat (with a delay to ensure org context is propagated)
    const initialHeartbeatTimeout = setTimeout(() => {
      sendHeartbeat(true)
    }, 500)

    // Set up recurring heartbeat
    heartbeatRef.current = setInterval(() => {
      sendHeartbeat(false)
    }, HEARTBEAT_INTERVAL)

    return () => {
      clearTimeout(initialHeartbeatTimeout)
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current)
        heartbeatRef.current = null
      }
      // Reset in-flight flag on cleanup
      heartbeatInFlightRef.current = false
    }
  }, [
    activeEntry?.id,
    activeEntry?.isPaused,
    currentOrganizationId,
    isExternalUser,
  ])

  const startTimer = useCallback(
    async (options?: {
      description?: string
      moduleType?: ModuleTypeEnum
      moduleId?: string
      moduleName?: string | null
      billable?: boolean
      roleId?: string | null
      linkedModules?: TimeEntryLinkedModule[]
      categoryId?: string | null
    }) => {
      if (!currentOrganization) {
        logger.error('Cannot start timer without current organization')
        throw new Error('Cannot start timer without an active organization')
      }

      const now = new Date()

      try {
        const linkedModules =
          options?.linkedModules ??
          (options?.moduleType && options?.moduleId
            ? [
                {
                  moduleType: options.moduleType,
                  moduleId: options.moduleId,
                },
              ]
            : undefined)
        const primaryModule =
          linkedModules && linkedModules.length === 1 ? linkedModules[0] : null
        const resolvedModuleType =
          options?.moduleType ?? primaryModule?.moduleType
        const resolvedModuleId = options?.moduleId ?? primaryModule?.moduleId
        const entryDate = createDateOnlyUTC(
          calendarDateInTimeZone(now, timezone),
        )

        // Always create a draft time entry in the database immediately
        // The recorded day uses the owner's zone; timestamps remain instants.
        const draftEntry = await createTimeEntryMutation.mutateAsync({
          data: {
            entryType: TimeEntryTypeEnum.TIMER,
            entryDate, // The owner's recorded calendar date
            startTime: now, // Start timestamp (includes both date and time)
            endTime: null,
            description: options?.description || '',
            // Omitted unless the caller chose a value, so the API applies
            // the resolved default (project > client > internal > org).
            ...(options?.billable !== undefined
              ? { billable: options.billable }
              : {}),
            status: 'DRAFT',
            moduleType: resolvedModuleType,
            moduleId: resolvedModuleId,
            moduleName: options?.moduleName ?? primaryModule?.moduleName,
            roleId: options?.roleId,
            ...(options?.categoryId ? { categoryId: options.categoryId } : {}),
            linkedModules,
          },
        })

        // Use the full draft entry with all fields
        const newEntry: ActiveTimeEntry = {
          ...draftEntry,
          elapsed: 0,
        }

        activeEntryRef.current = newEntry
        setActiveEntry(newEntry)
      } catch (error) {
        logger.error('Failed to start timer:', error)

        if (
          error instanceof ApiError &&
          error.status === 400 &&
          /active timer|already have a/.test(error.message)
        ) {
          await syncActiveTimerFromServer()
        }
        throw error
      }
    },
    [
      createTimeEntryMutation,
      currentOrganization,
      syncActiveTimerFromServer,
      timezone,
    ],
  )

  const stopTimer = useCallback(
    async (options?: {
      endTime?: Date
      description?: string
    }): Promise<TimeEntry | null> => {
      const currentEntry = activeEntryRef.current
      if (!currentEntry) return null

      try {
        const endTime = options?.endTime ?? new Date()

        // The API validates elapsed instants, including overnight timers and
        // repeated clocks. Comparing displayed HH:mm values is not valid here.
        // Update the existing draft entry with end time and finalize it
        const finalEntry = await updateTimeEntryMutation.mutateAsync({
          id: currentEntry.id,
          data: {
            endTime,
            description:
              options?.description ??
              (currentEntry.description || 'Time tracked'),
            moduleType: currentEntry.moduleType,
            moduleId: currentEntry.moduleId,
            linkedModules: currentEntry.linkedModules,
          },
        })

        clearLocalActiveTimerState()
        return finalEntry
      } catch (error) {
        logger.error('Failed to stop timer:', error)
        return null
      }
    },
    [clearLocalActiveTimerState, updateTimeEntryMutation],
  )

  const pauseTimer = async (): Promise<TimeEntry | null> => {
    const currentEntry = activeEntryRef.current
    if (!currentEntry) return null

    try {
      const updatedTimer = await pauseTimerMutation.mutateAsync(currentEntry.id)
      const elapsed = calculateElapsed(updatedTimer)
      const nextEntry = {
        ...updatedTimer,
        elapsed,
      }
      activeEntryRef.current = nextEntry
      setActiveEntry(nextEntry)
      return updatedTimer
    } catch (error) {
      logger.error('[TimeEntryProvider] Failed to pause timer:', error)
      return null
    }
  }

  const resumeTimer = async (): Promise<TimeEntry | null> => {
    const currentEntry = activeEntryRef.current
    if (!currentEntry) return null

    try {
      const updatedTimer = await resumeTimerMutation.mutateAsync(
        currentEntry.id,
      )
      const elapsed = calculateElapsed(updatedTimer)
      const nextEntry = {
        ...updatedTimer,
        elapsed,
      }
      activeEntryRef.current = nextEntry
      setActiveEntry(nextEntry)
      return updatedTimer
    } catch (error) {
      logger.error('[TimeEntryProvider] Failed to resume timer:', error)
      return null
    }
  }

  const switchTimerItem = useCallback(
    async (
      target: TimeEntryLinkedModule,
      options?: {
        linkedModules?: TimeEntryLinkedModule[]
        description?: string | null
        billable?: boolean
        roleId?: string | null
        categoryId?: string | null
      },
    ): Promise<TimeEntry | null> => {
      const currentEntry = activeEntryRef.current
      if (!currentEntry) return null

      try {
        const updatedTimer = await switchTimerItemMutation.mutateAsync({
          id: currentEntry.id,
          data: {
            moduleType: target.moduleType,
            moduleId: target.moduleId,
            moduleName: target.moduleName ?? null,
            linkedModules: options?.linkedModules ?? currentEntry.linkedModules,
            description: options?.description,
            billable: options?.billable,
            ...(options?.roleId !== undefined
              ? { roleId: options.roleId }
              : {}),
            ...(options?.categoryId !== undefined
              ? { categoryId: options.categoryId }
              : {}),
          },
        })
        const elapsed = calculateElapsed(updatedTimer)
        const nextEntry = {
          ...updatedTimer,
          elapsed,
        }
        activeEntryRef.current = nextEntry
        setActiveEntry(nextEntry)
        return updatedTimer
      } catch (error) {
        logger.error('[TimeEntryProvider] Failed to switch timer item:', error)
        return null
      }
    },
    [calculateElapsed, switchTimerItemMutation],
  )

  const discardTimer = async () => {
    const currentEntry = activeEntryRef.current
    if (!currentEntry) {
      return
    }

    try {
      clearLocalActiveTimerState()

      const timerSessionId = currentEntry.timerSessionId
      const sessionEntries = timerSessionId
        ? await timeEntryApi.getTimeEntries({
            timerSessionId,
            page: 1,
            limit: 250,
          } as Record<string, unknown>)
        : null

      const entryIds = new Set<string>([currentEntry.id])
      for (const entry of sessionEntries?.data || []) {
        if (entry.id) {
          entryIds.add(entry.id)
        }
      }

      await Promise.all(
        Array.from(entryIds).map(id => deleteTimeEntryMutation.mutateAsync(id)),
      )

      queryClient.setQueryData(
        queryKeys.timeEntries.active(currentOrganizationId),
        null,
      )

      const removeDeletedEntries = (cached: unknown) => {
        if (!cached) return cached

        if (Array.isArray(cached)) {
          return cached.filter(entry => {
            return !entry?.id || !entryIds.has(entry.id)
          })
        }

        if (typeof cached === 'object' && cached !== null && 'data' in cached) {
          const paginated = cached as PaginatedResponse<TimeEntry>
          if (!Array.isArray(paginated.data)) {
            return cached
          }

          return {
            ...paginated,
            data: paginated.data.filter(entry => !entryIds.has(entry.id)),
          }
        }

        return cached
      }

      queryClient.setQueriesData(
        { queryKey: queryKeys.timeEntries.lists() },
        removeDeletedEntries,
      )
      queryClient.setQueriesData(
        { queryKey: [...queryKeys.timeEntries.all, 'team'] },
        removeDeletedEntries,
      )

      for (const entryId of entryIds) {
        queryClient.removeQueries({
          queryKey: queryKeys.timeEntries.detail(entryId),
          exact: true,
        })
      }

      // Invalidate queries to refresh the time entries list
      queryClient.invalidateQueries({ queryKey: ['time-entries'] })
    } catch (error) {
      logger.error('[TimeEntryProvider] Failed to delete timer:', error)
      // Even if deletion fails, clear local state so user can continue
      clearLocalActiveTimerState()
    }
  }

  // Track the latest description to avoid overwriting with stale data
  const latestDescriptionRef = useRef<string | undefined>(undefined)

  // Create a debounced version of the API update call
  const debouncedUpdateApi = useMemo(() => {
    return debounce(async (id: string, updates: UpdateTimeEntryInput) => {
      try {
        const updatedTimer = await updateTimeEntryMutation.mutateAsync({
          id,
          data: updates,
        })

        // Only update state if the description hasn't changed since this request was made
        setActiveEntry(prev => {
          if (!prev) return null

          // If description was updated and it's different from what we just saved, keep the local version
          if (
            updates.description !== undefined &&
            latestDescriptionRef.current !== undefined &&
            latestDescriptionRef.current !== updates.description
          ) {
            const next = {
              ...prev,
              elapsed: calculateElapsed(prev),
            }
            activeEntryRef.current = next
            return next
          }

          // Otherwise, use the server response
          const elapsed = calculateElapsed(updatedTimer)
          const next = {
            ...updatedTimer,
            elapsed,
          }
          activeEntryRef.current = next
          return next
        })
      } catch (error) {
        logger.error('[TimeEntryProvider] Failed to update timer:', error)
      }
    }, 1500) // Increased to 1.5 seconds
    // Depend on actual props, not function closure that's fresh every render
  }, [updateTimeEntryMutation])

  const updateActiveEntry = useCallback(
    async (updates: Partial<ActiveTimeEntry>) => {
      const currentEntry = activeEntryRef.current
      if (!currentEntry) return

      // Track the latest description
      if (updates.description !== undefined) {
        latestDescriptionRef.current = updates.description
      }

      // Update local state immediately for responsiveness
      setActiveEntry(prev => {
        if (!prev) return null
        const next = { ...prev, ...updates }
        activeEntryRef.current = next
        return next
      })

      // Prepare update data (moduleName is local-only, not saved to DB)
      const updateData: UpdateTimeEntryInput = {}
      if (updates.description !== undefined)
        updateData.description = updates.description
      if (updates.moduleType !== undefined)
        updateData.moduleType = updates.moduleType
      if (updates.moduleId !== undefined) updateData.moduleId = updates.moduleId
      if (updates.linkedModules !== undefined)
        updateData.linkedModules = updates.linkedModules
      if (updates.roleId !== undefined) updateData.roleId = updates.roleId
      if (updates.categoryId !== undefined)
        updateData.categoryId = updates.categoryId
      if (updates.billable !== undefined) updateData.billable = updates.billable

      // Skip API call if only moduleName changed (it's a local display property)
      if (Object.keys(updateData).length === 0) {
        return
      }

      // For description updates, use debounced API call
      if (
        updates.description !== undefined &&
        Object.keys(updates).length === 1
      ) {
        debouncedUpdateApi(currentEntry.id, updateData)
      } else {
        // For other updates (module changes), update immediately
        try {
          const updatedTimer = await updateTimeEntryMutation.mutateAsync({
            id: currentEntry.id,
            data: updateData,
          })
          const elapsed = calculateElapsed(updatedTimer)
          const nextEntry = {
            ...updatedTimer,
            elapsed,
            // Preserve moduleName if it was set locally
            ...(updates.moduleName !== undefined && {
              moduleName: updates.moduleName,
            }),
          }
          activeEntryRef.current = nextEntry
          setActiveEntry(nextEntry)
        } catch (error) {
          logger.error('[TimeEntryProvider] Failed to update timer:', error)
          throw error
        }
      }
    },
    [updateTimeEntryMutation, calculateElapsed, debouncedUpdateApi],
  )

  const clearTimerData = () => {
    clearLocalActiveTimerState()
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
    }
  }, [])

  const value: TimeEntryContextType = {
    activeEntry,
    isTracking: !!activeEntry && !activeEntry.isPaused,
    startTimer,
    stopTimer,
    pauseTimer,
    resumeTimer,
    switchTimerItem,
    discardTimer,
    updateActiveEntry,
    clearTimerData,
    isPaused: activeEntry?.isPaused || false,
    isInitialized,
  }

  return (
    <TimeEntryContext.Provider value={value}>
      {children}
    </TimeEntryContext.Provider>
  )
}

export function useTimeEntry({
  liveElapsed = true,
  intervalMs = 1000,
  background = false,
}: {
  liveElapsed?: boolean
  intervalMs?: 1000 | 60000
  background?: boolean
} = {}) {
  const context = useContext(TimeEntryContext)
  if (!context) {
    throw new Error('useTimeEntry must be used within a TimeEntryProvider')
  }
  const timer = context.activeEntry
  const now = useDisplayClock({
    enabled: liveElapsed && !!timer && !timer.isPaused,
    intervalMs,
    background,
    phaseMs: timer?.startTime
      ? new Date(timer.startTime).getTime() + timer.pausedDuration * 1000
      : 0,
  })
  return {
    ...context,
    activeEntry:
      liveElapsed && timer
        ? { ...timer, elapsed: Math.max(0, calculateElapsed(timer, now)) }
        : timer,
  }
}
